import { useEffect, useMemo, useState } from "react";
import type { Game } from "@engine/core/Game";
import type { Entity } from "@engine/ecs/components";
import { Tooltip } from "@engine/ui";

interface Props {
  game?: Game | undefined;
}

export function Demo6Hud({ game }: Props) {
  const [hovered, setHovered] = useState<Entity | null>(null);
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!game) {
      setHovered(null);
      setAnchor(null);
      return;
    }
    const canvas = game.app.canvas;
    const onMove = (ev: MouseEvent): void => {
      const rect = canvas.getBoundingClientRect();
      const sx = ev.clientX - rect.left;
      const sy = ev.clientY - rect.top;
      const world = game.camera.screenToWorld({ x: sx, y: sy });
      const picked = pickNearest(game, world.x, world.y);
      setHovered(picked);
      setAnchor(picked ? { x: ev.clientX, y: ev.clientY } : null);
    };
    const onLeave = (): void => {
      setHovered(null);
      setAnchor(null);
    };
    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("mouseleave", onLeave);
    return () => {
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("mouseleave", onLeave);
    };
  }, [game]);

  const tooltipBody = useMemo(() => (hovered ? renderTooltip(hovered) : null), [hovered]);

  return <Tooltip anchor={anchor}>{tooltipBody}</Tooltip>;
}

function pickNearest(game: Game, wx: number, wy: number): Entity | null {
  return game.inspector.pick(wx, wy, 36);
}

function renderTooltip(e: Entity) {
  const hp = e.health ? `${Math.round(e.health.current)}/${Math.round(e.health.max)}` : null;
  return (
    <div>
      <div style={{ fontWeight: 600 }}>{e.displayName ?? e.kind ?? "(unknown)"}</div>
      <div style={{ opacity: 0.7, fontSize: 11 }}>{e.kind ?? "—"}{e.id ? ` · ${e.id}` : ""}</div>
      {hp && <div style={{ marginTop: 4, color: "#86efac" }}>HP {hp}</div>}
    </div>
  );
}
